import * as z from 'zod/v4';

export const EmailMarketingConsentBasisSchema = z.enum(['CONSENT', 'SOFT_OPT_IN', 'TRANSACTIONAL_ONLY', 'UNKNOWN']);
export const EmailMarketingSuppressionReasonSchema = z.enum(['MANUAL_UNSUB', 'ONE_CLICK_UNSUB', 'HARD_BOUNCE', 'COMPLAINT', 'ADMIN_BLOCK']);
export const EmailMarketingCampaignTypeSchema = z.enum(['BROADCAST', 'AUTOMATION']);
export const EmailMarketingAutomationTriggerSchema = z.enum([
  'ABANDONED_CART',
  'POST_PURCHASE',
  'WELCOME',
  'WIN_BACK',
  'RE_ENGAGEMENT',
  'TAG_ADDED',
]);

export const EmailMarketingContactCreateSchema = z.object({
  email: z.email(),
  name: z.string().trim().min(1).nullable().optional(),
  customer_id: z.string().uuid().nullable().optional(),
  tags: z.array(z.string().trim().min(1)).default([]),
  consent_basis: EmailMarketingConsentBasisSchema.default('UNKNOWN'),
  source: z.string().trim().min(1).default('dashboard'),
});

export const EmailMarketingTemplateCreateSchema = z.object({
  name: z.string().trim().min(1),
  subject: z.string().trim().min(1),
  preheader: z.string().nullable().optional(),
  mjml_source: z.string().trim().min(1),
  builder_project_json: z.record(z.string(), z.unknown()).nullable().optional(),
  thumbnail_url: z.url().nullable().optional(),
});

export const EmailMarketingTemplatePreviewSchema = z.object({
  mjml_source: z.string().trim().min(1),
  sample_variables: z.record(z.string(), z.unknown()).optional(),
});

export const EmailMarketingTemplateTestSendSchema = z.object({
  recipient_email: z.email(),
  sample_variables: z.record(z.string(), z.unknown()).optional(),
});

export const EmailMarketingSettingsUpdateSchema = z.object({
  marketing_enabled: z.boolean().optional(),
  default_from_name: z.string().trim().min(1).nullable().optional(),
  default_from_email: z.email().nullable().optional(),
  default_reply_to_email: z.email().nullable().optional(),
  physical_address: z.string().trim().min(1).nullable().optional(),
  physical_address_line2: z.string().trim().min(1).nullable().optional(),
  physical_city: z.string().trim().min(1).nullable().optional(),
  physical_postal_code: z.string().trim().min(1).nullable().optional(),
  physical_country: z.string().trim().length(2).nullable().optional(),
  daily_limit: z.number().int().positive().optional(),
  monthly_soft_cap: z.number().int().positive().optional(),
});

export const EmailMarketingCampaignCreateSchema = z.object({
  type: EmailMarketingCampaignTypeSchema.default('BROADCAST'),
  name: z.string().trim().min(1),
  subject: z.string().trim().min(1),
  template_id: z.string().uuid(),
  list_id: z.string().uuid().nullable().optional(),
  segment_id: z.string().uuid().nullable().optional(),
  from_name: z.string().trim().min(1),
  from_email: z.email(),
  reply_to_email: z.email().nullable().optional(),
  schedule_at: z.string().datetime().nullable().optional(),
  target_emails: z.array(z.email()).max(0, 'Direct recipient entry is disabled. Use lists or segments built from Shoppex customers and paid order buyers.').default([]),
  idempotency_key: z.string().trim().min(1).nullable().optional(),
});

export const EmailMarketingAutomationCreateSchema = z.object({
  name: z.string().trim().min(1),
  subject: z.string().trim().min(1),
  template_id: z.string().uuid(),
  from_name: z.string().trim().min(1),
  from_email: z.email(),
  reply_to_email: z.email().nullable().optional(),
  trigger: EmailMarketingAutomationTriggerSchema,
  delay_seconds: z.number().int().min(0).default(0),
  conditions: z.record(z.string(), z.unknown()).default({}),
});

export const EmailMarketingAutomationTriggerEventSchema = z.object({
  trigger: EmailMarketingAutomationTriggerSchema,
  email: z.email(),
  customer_id: z.string().uuid().nullable().optional(),
  name: z.string().trim().min(1).nullable().optional(),
  consent_basis: EmailMarketingConsentBasisSchema.default('UNKNOWN'),
  variables: z.record(z.string(), z.unknown()).default({}),
  idempotency_key: z.string().trim().min(1).nullable().optional(),
});

export const EmailMarketingSuppressionCreateSchema = z.object({
  email: z.email(),
  reason: EmailMarketingSuppressionReasonSchema.default('ADMIN_BLOCK'),
  notes: z.string().nullable().optional(),
});

export type EmailMarketingContactCreate = z.infer<typeof EmailMarketingContactCreateSchema>;
export type EmailMarketingTemplateCreate = z.infer<typeof EmailMarketingTemplateCreateSchema>;
export type EmailMarketingTemplatePreview = z.infer<typeof EmailMarketingTemplatePreviewSchema>;
export type EmailMarketingTemplateTestSend = z.infer<typeof EmailMarketingTemplateTestSendSchema>;
export type EmailMarketingSettingsUpdate = z.infer<typeof EmailMarketingSettingsUpdateSchema>;
export type EmailMarketingCampaignCreate = z.infer<typeof EmailMarketingCampaignCreateSchema>;
export type EmailMarketingAutomationCreate = z.infer<typeof EmailMarketingAutomationCreateSchema>;
export type EmailMarketingAutomationTriggerEvent = z.infer<typeof EmailMarketingAutomationTriggerEventSchema>;
export type EmailMarketingSuppressionCreate = z.infer<typeof EmailMarketingSuppressionCreateSchema>;
